import { Button } from "@veil/ui/components/button";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import Globe from "lucide-react/dist/esm/icons/globe";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState } from "react";

/** How long to poll the new origin before surfacing the manual link. */
const POLL_TIMEOUT_MS = 45_000;
const POLL_INTERVAL_MS = 1_500;

/**
 * Full-screen interstitial shown after "Review & launch" completes when the
 * wizard was driven from a different origin than the configured hostname
 * (typically http://<raw-ip>/). The hardened render that just kicked off will
 * cut this origin off, so we wait for the new origin to answer, then redirect
 * through the one-time session handoff so the operator stays logged in.
 */
export function ConsoleMoved({
	consoleUrl,
	handoffPath,
}: {
	consoleUrl: string;
	handoffPath: string | null;
}) {
	const reduce = useReducedMotion();
	const [phase, setPhase] = useState<"waiting" | "redirecting" | "manual">(
		"waiting",
	);
	const redirected = useRef(false);

	const targetUrl = handoffPath ? `${consoleUrl}${handoffPath}` : consoleUrl;

	useEffect(() => {
		let cancelled = false;
		const startedAt = Date.now();

		const redirect = () => {
			if (redirected.current) return;
			redirected.current = true;
			setPhase("redirecting");
			window.location.href = targetUrl;
		};

		// Poll the new origin until it answers. `no-cors` gives an opaque
		// response — we can't read the status, but resolving at all means TLS +
		// routing are up, which is all the redirect needs.
		const tick = async () => {
			if (cancelled) return;
			try {
				await fetch(`${consoleUrl}/health`, {
					mode: "no-cors",
					cache: "no-store",
				});
				redirect();
				return;
			} catch {
				// Not up yet (or this origin was already cut off — the redirect
				// below still applies once the timer fires).
			}
			if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
				if (!cancelled) setPhase("manual");
				return;
			}
			setTimeout(tick, POLL_INTERVAL_MS);
		};
		void tick();

		return () => {
			cancelled = true;
		};
	}, [consoleUrl, targetUrl]);

	return (
		<div className="flex min-h-screen items-center justify-center bg-muted/30 px-5">
			<motion.div
				initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: reduce ? 0.12 : 0.3, ease: [0.25, 1, 0.5, 1] }}
				className="w-full max-w-md rounded-xl border border-border/70 bg-card p-8 text-center shadow-sm"
			>
				<span className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
					<Globe className="size-5" />
				</span>

				<h1 className="mt-5 font-heading text-lg font-semibold tracking-tight">
					Your console has moved
				</h1>
				<p className="mt-2 text-sm leading-relaxed text-muted-foreground">
					Setup is complete and the edge is now locked down. The console lives
					at its configured address from here on:
				</p>

				<code className="mt-4 block truncate rounded-md bg-muted px-3 py-2 font-mono text-sm">
					{consoleUrl}
				</code>

				{phase === "manual" ? (
					<p className="mt-4 text-xs leading-relaxed text-muted-foreground">
						The new address isn't answering yet — DNS may still be propagating.
						Open it manually once it resolves; you may need to log in again.
					</p>
				) : (
					<p className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
						<Loader2 className="size-3.5 animate-spin" />
						{phase === "redirecting"
							? "Redirecting…"
							: "Waiting for the new address to come up…"}
					</p>
				)}

				<Button asChild className="mt-5 h-11 w-full">
					<a href={targetUrl}>
						Open the console
						<ArrowRight data-icon="inline-end" />
					</a>
				</Button>

				<p className="mt-3 text-[0.7rem] leading-relaxed text-muted-foreground">
					Your session carries over automatically. This page (the raw IP
					address) no longer serves the console.
				</p>
			</motion.div>
		</div>
	);
}
